/**
 * Modal forms for the Organisation console.
 *
 * All writes are server-authoritative (see useOrg.ts): the dialog stays open and
 * surfaces the server's message on failure (a rejected cycle, a duplicate name,
 * a 409 delete-with-children) and only closes once the server confirms. No
 * optimistic close. 4xx errors show no Retry (retrying reproduces them); the
 * mutation hooks already invalidate the right queries on success.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { UserSelect } from '@/components/admin/UserSelect';
import { PatientPicker } from '@/components/clinical/PatientPicker';
import { usePatients, usePatientsConfig } from '@/hooks/usePatients';
import { locationIndex, useEnumLabel } from '@/components/clinical/encounterDisplay';
import { errMessage, isForbidden } from './OrgUi';
import { RequestAccessInline } from './AccessRequests';
import { buildTree, descendantIds, flattenVisible } from './tree';
import {
  useLocationKinds,
  useKnownResourceTypes,
  useSelfAssign,
  useRoles,
  useCreateLocation,
  useUpdateLocation,
  useMoveLocation,
  useAddLocationMember,
  useAdmitPatient,
  useLocations,
  useCreateRole,
  useUpdateRole,
  useAddRoleMember,
  useAddRoleGrant,
} from './useOrg';
import type { Location, OrgRole, PrincipalType } from '@/api/endpoints/org';

const ORG_ROLES: OrgRole[] = ['viewer', 'editor', 'owner'];

const selectClass =
  'h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm focus:border-primary focus:outline-none';

function DialogError({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMessage(error)}</p>;
}

// -----------------------------------------------------------------------------
// Labels editor (shared) — key/value rows assembled into a Record on submit.
// -----------------------------------------------------------------------------

interface LabelRow {
  key: string;
  value: string;
}

function toRows(labels: Record<string, string> | undefined): LabelRow[] {
  return Object.entries(labels ?? {}).map(([key, value]) => ({ key, value }));
}

function toRecord(rows: LabelRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { key, value } of rows) {
    const k = key.trim();
    if (k) out[k] = value;
  }
  return out;
}

function LabelsEditor({ rows, onChange }: { rows: LabelRow[]; onChange: (rows: LabelRow[]) => void }) {
  const { t } = useTranslation();
  const set = (i: number, patch: Partial<LabelRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      <span className="text-xs font-medium text-neutral-500">{t('org.labels.title', 'Labels (optional)')}</span>
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            placeholder={t('org.labels.keyPlaceholder', 'key')}
            value={row.key}
            onChange={(e) => set(i, { key: e.target.value })}
            className="h-8"
          />
          <Input
            placeholder={t('org.labels.valuePlaceholder', 'value')}
            value={row.value}
            onChange={(e) => set(i, { value: e.target.value })}
            className="h-8"
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-neutral-400 hover:text-red-600"
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
            aria-label={t('org.labels.remove', 'Remove label')}
          >
            ✕
          </Button>
        </div>
      ))}
      <Button variant="ghost" size="sm" className="h-7 px-2 text-primary" onClick={() => onChange([...rows, { key: '', value: '' }])}>
        + {t('org.labels.addLabel', 'Add label')}
      </Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Location: create / edit
// -----------------------------------------------------------------------------

export function LocationFormDialog({
  open,
  onOpenChange,
  /** Present = edit that unit. Absent = create. */
  location,
  /** For create: the parent to nest under (undefined = a new root unit). */
  parentId,
  parentName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: Location;
  parentId?: string;
  parentName?: string;
}) {
  const { t } = useTranslation();
  const isEdit = !!location;
  const kinds = useLocationKinds();
  const create = useCreateLocation();
  const update = useUpdateLocation();
  const mutation = isEdit ? update : create;

  const [name, setName] = useState('');
  const [kind, setKind] = useState('');
  const [labels, setLabels] = useState<LabelRow[]>([]);

  // Reset the form each time the dialog opens for a (possibly different) target.
  useEffect(() => {
    if (!open) return;
    setName(location?.name ?? '');
    setKind(location?.kind ?? kinds[0] ?? '');
    setLabels(toRows(location?.labels));
    mutation.reset();
    // Fires on OPEN only. `mutation` is a react-query mutation object whose
    // identity changes on every status transition, so depending on it would
    // re-clear the form mid-submit; `kinds` arriving later is handled by the
    // backfill effect below, which does not clobber typed input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, location]);

  // If /config finishes loading AFTER the create dialog is already open, the
  // kind picker started empty — backfill the default once kinds arrive, without
  // clobbering a name/labels the user may have already typed.
  useEffect(() => {
    if (open && !isEdit && !kind && kinds.length > 0) setKind(kinds[0]);
  }, [open, isEdit, kind, kinds]);

  const canSubmit = name.trim().length > 0 && (isEdit || kind.length > 0);

  const submit = () => {
    if (!canSubmit) return;
    if (isEdit) {
      update.mutate(
        { id: location.id, body: { name: name.trim(), labels: toRecord(labels) } },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      create.mutate(
        { name: name.trim(), kind, parent_id: parentId, labels: toRecord(labels) },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? t('org.location.editTitle', 'Edit unit') : t('org.location.newTitle', 'New unit')}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? t('org.location.editDesc', 'Rename this unit or adjust its labels. Its kind and place in the tree do not change here.')
              : parentName
                ? t('org.location.newChildDesc', { parent: parentName })
                : t('org.location.newRootDesc', 'A new top-level unit (e.g. a hospital).')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-neutral-500">{t('common.name', 'Name')}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('org.location.namePlaceholder', 'e.g. ICU 2')} autoFocus />
          </div>

          {!isEdit && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-neutral-500">{t('org.location.kindLabel', 'Kind')}</label>
              <select className={selectClass} value={kind} onChange={(e) => setKind(e.target.value)}>
                {kinds.length === 0 && <option value="">{t('org.location.noKinds', 'No kinds configured')}</option>}
                {kinds.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
          )}

          <LabelsEditor rows={labels} onChange={setLabels} />
          <DialogError error={mutation.error} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit || mutation.isPending}>
            {mutation.isPending ? <Spinner className="h-4 w-4" /> : isEdit ? t('common.save', 'Save') : t('common.create', 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Location: move (re-parent)
// -----------------------------------------------------------------------------

export function MoveLocationDialog({
  open,
  onOpenChange,
  location,
  allLocations,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: Location;
  allLocations: Location[];
}) {
  const { t } = useTranslation();
  const move = useMoveLocation();
  const [parentId, setParentId] = useState<string>(location.parent_id ?? '');

  useEffect(() => {
    if (open) {
      setParentId(location.parent_id ?? '');
      move.reset();
    }
    // Fires on OPEN only. `move` is a react-query mutation whose identity changes
    // on every status transition; as a dependency it would reset the picker back
    // to the current parent while a move is in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, location]);

  // A unit cannot move under itself or any of its descendants — the server
  // rejects that as a cycle, but disabling it up front is clearer.
  const blocked = useMemo(() => {
    const tree = buildTree(allLocations);
    const self = flattenVisible(tree, new Set(allLocations.map((l) => l.id))).find(
      (n) => n.location.id === location.id,
    );
    const ids = self ? descendantIds(self) : new Set<string>();
    ids.add(location.id);
    return ids;
  }, [allLocations, location.id]);

  const options = useMemo(
    () =>
      flattenVisible(buildTree(allLocations), new Set(allLocations.map((l) => l.id)))
        .filter((n) => !blocked.has(n.location.id))
        .map((n) => ({ id: n.location.id, label: `${'  '.repeat(n.depth)}${n.location.name}` })),
    [allLocations, blocked],
  );

  const submit = () =>
    move.mutate(
      { id: location.id, parentId: parentId || null },
      { onSuccess: () => onOpenChange(false) },
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.move.title', { name: location.name })}</DialogTitle>
          <DialogDescription>
            {t('org.move.desc', 'Choose a new parent unit. Access cascades from the parent down, so moving a unit moves its whole subtree with it.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <select className={selectClass} value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">{t('org.move.topLevel', '— Top level (no parent) —')}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <DialogError error={move.error} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={move.isPending || parentId === (location.parent_id ?? '')}>
            {move.isPending ? <Spinner className="h-4 w-4" /> : t('org.move.button', 'Move')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Location: add member (a user or a role, at a role level)
// -----------------------------------------------------------------------------

export function AddLocationMemberDialog({
  open,
  onOpenChange,
  locationId,
  locationName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
  locationName: string;
}) {
  const { t } = useTranslation();
  const add = useAddLocationMember();
  const { data: rolesData } = useRoles();
  const roles = rolesData ?? [];

  const [principalType, setPrincipalType] = useState<PrincipalType>('user');
  const [principalId, setPrincipalId] = useState('');
  const [role, setRole] = useState<OrgRole>('viewer');

  useEffect(() => {
    if (open) {
      setPrincipalType('user');
      setPrincipalId('');
      setRole('viewer');
      add.reset();
    }
    // Fires on OPEN only. `add` is a react-query mutation whose identity changes
    // on every status transition; as a dependency it would wipe the admin's
    // principal/role choice the moment they submitted it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    if (!principalId) return;
    add.mutate(
      { id: locationId, member: { principal_type: principalType, principal_id: principalId, role } },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.assign.title', { name: locationName })}</DialogTitle>
          <DialogDescription>
            {t('org.assign.desc', 'Give a person or a whole role access to this unit. Viewer works here · editor manages it · owner runs it. Access cascades to every sub-unit.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            {(['user', 'group'] as PrincipalType[]).map((pt) => (
              <button
                key={pt}
                onClick={() => {
                  setPrincipalType(pt);
                  setPrincipalId('');
                }}
                className={`flex-1 rounded-md border px-3 py-1.5 text-sm transition-colors ${
                  principalType === pt
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                {pt === 'user' ? t('org.assign.person', 'Person') : t('org.assign.role', 'Role')}
              </button>
            ))}
          </div>

          {principalType === 'user' ? (
            <UserSelect onSelect={setPrincipalId} onClear={() => setPrincipalId('')} />
          ) : (
            <select className={selectClass} value={principalId} onChange={(e) => setPrincipalId(e.target.value)}>
              <option value="">{t('org.assign.selectRole', 'Select a role…')}</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}

          <div className="space-y-1">
            <label className="text-xs font-medium text-neutral-500">{t('org.assign.accessLevel', 'Access level')}</label>
            <select className={selectClass} value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
              {ORG_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`org.accessLevel.${r}`, r)}
                </option>
              ))}
            </select>
          </div>

          <DialogError error={add.error} />
          {isForbidden(add.error) && <RequestAccessInline locationId={locationId} />}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={!principalId || add.isPending}>
            {add.isPending ? <Spinner className="h-4 w-4" /> : t('org.assign.button', 'Assign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Location: admit a patient
// -----------------------------------------------------------------------------

export function AdmitPatientDialog({
  open,
  onOpenChange,
  locationId,
  locationName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
  locationName: string;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const admit = useAdmitPatient();
  // This IS the re-admission picker (per the dialog description below: "If
  // they are already in another unit, this transfers them here") — a
  // discharged patient being re-admitted is the exact population it exists
  // for, so it must include them ('all'), not just usePatients()'s
  // in_progress default. Non-active rows are badged so the nurse can tell a
  // re-admission from a transfer at a glance.
  const { data: patients, isLoading, isError, refetch } = usePatients('all');
  const patientsConfig = usePatientsConfig();
  const { data: locations } = useLocations();
  const locationById = useMemo(() => locationIndex(locations), [locations]);

  useEffect(() => {
    if (open) admit.reset();
    // Fires on OPEN only. `admit` is a react-query mutation whose identity changes
    // on every status transition, so depending on it would clear the error the
    // dialog is showing (and re-enter idle) as soon as the admission failed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.admit.title', { name: locationName })}</DialogTitle>
          <DialogDescription>
            {t('org.admit.desc', 'Select a patient to place in this unit. If they are already in another unit, this transfers them here.')}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto">
          <PatientPicker
            patients={patients}
            isLoading={isLoading}
            isError={isError}
            onRetry={() => void refetch()}
            labelFormat={patientsConfig?.label_format}
            locationById={locationById}
            renderBadge={(p) =>
              p.status !== 'in_progress' ? (
                <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
                  {label('status', p.status)}
                </span>
              ) : null
            }
            onSelect={(p) =>
              admit.mutate(
                { locationId, patientId: p.token },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          />
        </div>

        <DialogError error={admit.error} />
        {/* The 403 deny message names this exact next step ("…or submit an
            access request for this unit") — surface the affordance with it. */}
        {isForbidden(admit.error) && <RequestAccessInline locationId={locationId} />}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Role: create / edit
// -----------------------------------------------------------------------------

export function RoleFormDialog({
  open,
  onOpenChange,
  role,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: { id: string; name: string; description: string; labels: Record<string, string> };
}) {
  const { t } = useTranslation();
  const isEdit = !!role;
  const create = useCreateRole();
  const update = useUpdateRole();
  const mutation = isEdit ? update : create;

  // Onboarding self-assign marker, sourced from config so the toggle and the
  // backend enforcement share one key (no hardcoded magic string). The toggle is
  // shown only when self-assign is enabled; the marker label is split OUT of the
  // raw Labels editor so it is edited in exactly one place.
  const selfAssign = useSelfAssign();
  const roleLabel = selfAssign?.role_label ?? '';
  const showSelfSelect = !!(selfAssign?.enabled && roleLabel);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [labels, setLabels] = useState<LabelRow[]>([]);
  const [selfSelectable, setSelfSelectable] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    const all = role?.labels ?? {};
    setSelfSelectable(roleLabel ? all[roleLabel] === 'true' : false);
    // Hide the marker key from the raw editor so it is not edited twice.
    setLabels(toRows(Object.fromEntries(Object.entries(all).filter(([k]) => k !== roleLabel))));
    mutation.reset();
    // Fires on OPEN (or on a change of target/marker key) only. `mutation` is a
    // react-query mutation whose identity changes on every status transition, so
    // depending on it would re-clear name/description/labels mid-save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role, roleLabel]);

  const canSubmit = name.trim().length > 0;

  const submit = () => {
    if (!canSubmit) return;
    const labelRecord = toRecord(labels);
    // Fold the toggle back into labels under the config-sourced key.
    if (roleLabel) {
      if (selfSelectable) labelRecord[roleLabel] = 'true';
      else delete labelRecord[roleLabel];
    }
    const body = { name: name.trim(), description: description.trim(), labels: labelRecord };
    if (isEdit) {
      update.mutate({ id: role.id, body }, { onSuccess: () => onOpenChange(false) });
    } else {
      create.mutate(body, { onSuccess: () => onOpenChange(false) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? t('org.role.editTitle', 'Edit role') : t('org.role.newTitle', 'New role')}</DialogTitle>
          <DialogDescription>
            {t('org.role.desc', 'A role is a named group of people. Assign it to units and resources, and its members inherit the access.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-neutral-500">{t('common.name', 'Name')}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('org.role.namePlaceholder', 'e.g. ICU Nurses')} autoFocus />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-neutral-500">{t('org.role.descLabel', 'Description (optional)')}</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('org.role.descPlaceholder', 'What this role is for')} />
          </div>
          {showSelfSelect && (
            <label className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={selfSelectable}
                onChange={(e) => setSelfSelectable(e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              {t('org.role.selfSelectable', 'Self-selectable during onboarding')}
            </label>
          )}
          <LabelsEditor rows={labels} onChange={setLabels} />
          <DialogError error={mutation.error} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit || mutation.isPending}>
            {mutation.isPending ? <Spinner className="h-4 w-4" /> : isEdit ? t('common.save', 'Save') : t('common.create', 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Role: add member
// -----------------------------------------------------------------------------

export function AddRoleMemberDialog({
  open,
  onOpenChange,
  roleId,
  roleName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string;
  roleName: string;
}) {
  const { t } = useTranslation();
  const add = useAddRoleMember();
  const [userId, setUserId] = useState('');

  useEffect(() => {
    if (open) {
      setUserId('');
      add.reset();
    }
    // Fires on OPEN only. `add` is a react-query mutation whose identity changes
    // on every status transition; as a dependency it would clear the selected
    // user out from under the in-flight add.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.roleMember.title', { name: roleName })}</DialogTitle>
          <DialogDescription>{t('org.roleMember.desc', 'Everyone in this role inherits its access to units and resources.')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <UserSelect onSelect={setUserId} onClear={() => setUserId('')} />
          <DialogError error={add.error} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => userId && add.mutate({ id: roleId, userId }, { onSuccess: () => onOpenChange(false) })}
            disabled={!userId || add.isPending}
          >
            {add.isPending ? <Spinner className="h-4 w-4" /> : t('common.add', 'Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Role: grant access to a resource
// -----------------------------------------------------------------------------

export function AddGrantDialog({
  open,
  onOpenChange,
  roleId,
  roleName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string;
  roleName: string;
}) {
  const { t } = useTranslation();
  const add = useAddRoleGrant();
  const resourceTypes = useKnownResourceTypes();

  const [resourceType, setResourceType] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [role, setRole] = useState<OrgRole>('viewer');

  useEffect(() => {
    if (open) {
      setResourceType(resourceTypes[0] ?? '');
      setResourceId('');
      setRole('viewer');
      add.reset();
    }
    // Fires on OPEN only. `add` is a react-query mutation whose identity changes
    // on every status transition, and `resourceTypes` is a fresh array from
    // /config on every render — either dependency would reset the grant form the
    // admin is filling in back to defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSubmit = resourceType.length > 0 && resourceId.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.grant.title', { name: roleName })}</DialogTitle>
          <DialogDescription>
            {t('org.grant.desc', 'Let everyone in this role reach a specific resource at the chosen level.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-neutral-500">{t('org.grant.resourceTypeLabel', 'Resource type')}</label>
            <select className={selectClass} value={resourceType} onChange={(e) => setResourceType(e.target.value)}>
              {resourceTypes.length === 0 && <option value="">{t('org.grant.noResourceTypes', 'No resource types configured')}</option>}
              {resourceTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-neutral-500">{t('org.grant.resourceIdLabel', 'Resource ID')}</label>
            <Input value={resourceId} onChange={(e) => setResourceId(e.target.value)} placeholder={t('org.grant.resourceIdPlaceholder', "the resource's id")} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-neutral-500">{t('org.assign.accessLevel', 'Access level')}</label>
            <select className={selectClass} value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
              {ORG_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`org.accessLevel.${r}`, r)}
                </option>
              ))}
            </select>
          </div>
          <DialogError error={add.error} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() =>
              canSubmit &&
              add.mutate(
                { id: roleId, grant: { resource_type: resourceType, resource_id: resourceId.trim(), role } },
                { onSuccess: () => onOpenChange(false) },
              )
            }
            disabled={!canSubmit || add.isPending}
          >
            {add.isPending ? <Spinner className="h-4 w-4" /> : t('org.grant.button', 'Grant')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
